import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Endpoint } from '@enums/endpoint.enum';
import { StorageHelper } from '@helpers/storage.helper';
import { AuthResponse } from '@models/auth-response.model';
import { User } from '@models/user.model';
import { RoleHelper } from '@helpers/role.helper';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { AuthResult } from '@models/auth-result.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private http: HttpClient) { }

  authenticate(email: string, password: string): Observable<AuthResult> {
    const url = `${environment.apiBaseUrl}${Endpoint.TOKEN}`;

    return this.http.get(url, { headers: { Authorization: 'Basic ' + btoa(`${email}:${password}`) } }).pipe(
      map((response: any) => {
        if (response.requires_activation) {
          return new AuthResult({ status: 'activation_required' });
        }
        if (!response.data) {
          return new AuthResult({ status: 'error' });
        }

        this.setAccessToken(new AuthResponse({ ...response.data, timestamp: Date.now() }));
        return new AuthResult({ status: 'success' });
      }),
      catchError((error) => {
        if (error.error?.requires_activation) {
          return of(new AuthResult({
            status:             'activation_required',
            activationViaEmail: error.error.activation_via_email,
            activationToken:    error.error.activation_token,
          }));
        }
        return of(new AuthResult({ status: 'error' }));
      })
    );
  }

  logout(): Observable<boolean> {

    const url = `${environment.apiBaseUrl}${Endpoint.REVOKE_TOKEN}`;

    return this.http.delete(url).pipe(
      map((response: any) => {
        this.removeAccessToken();

        return true;
      }),
      catchError(error => {
        return of(false);
      })
    );
  }

  refreshToken(): Observable<boolean> {
    const url = `${environment.apiBaseUrl}${Endpoint.REFRESH_TOKEN}`;
    const token = this.getAccessToken();

    return this.http.get(url, { headers: { Authorization: `Bearer ${token?.jwt}` } }).pipe(
      map((response: any) => {
        this.setAccessToken(new AuthResponse({...response.data, timestamp: Date.now()}));
        return true;
      })
    );
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();

    return !!token?.jwt && !token?.isExpired();
  }

  getAccessToken() {
    return StorageHelper.getToken() || new AuthResponse();
  }

  removeAccessToken() {
    StorageHelper.removeToken();
  }

  setAccessToken(auth: AuthResponse) {
    StorageHelper.setToken(auth);
  }

  isCurrentUser(user: User): boolean {
    return this.getAccessToken().currentUserId === user.id;
  }

  canCurrentUserEditUser(target: User): boolean {
    return RoleHelper.canEdit(this.getAccessToken().primaryRole, target.primaryRole);
  }

  getAssignableRoles(): string[] {
    switch (this.getAccessToken().primaryRole) {
      case User.ROLE_ADMIN: {
        return [User.ROLE_ADMIN, User.ROLE_INSTANCE_CONFIGURATOR, User.ROLE_CLIENT_ADMIN, User.ROLE_MARKETER, User.ROLE_PROPERTY_MANAGER];
      }
      case User.ROLE_CLIENT_ADMIN: {
        return [User.ROLE_CLIENT_ADMIN, User.ROLE_MARKETER, User.ROLE_PROPERTY_MANAGER];
      }
      default: {
        return [];
      }
    }
  }

  activateAccount(password: string, token?: string): Observable<boolean> {
      const url = `${environment.apiBaseUrl}${Endpoint.ACTIVATE_ACCOUNT}`;

      return this.http.post(url, { password, ...(token && { token }) }).pipe(
          map((response: any) => {
              if (response?.data) {
                  this.setAccessToken(new AuthResponse({ ...response.data, timestamp: Date.now() }));
              }
              return true;
          }),
          catchError(() => of(false))
      );
  }

  forgotPassword(email: string): Observable<boolean> {
      const url = `${environment.apiBaseUrl}${Endpoint.FORGOT_PASSWORD}`;

      return this.http.post(url, { email }).pipe(
          map(() => true)
      );
  }

  resetPassword(password: string, token: string): Observable<boolean> {
      const url = `${environment.apiBaseUrl}${Endpoint.RESET_PASSWORD}`;

      return this.http.post(url, { password, token }).pipe(
          map((response: any) => {
              if (response?.data) {
                  this.setAccessToken(new AuthResponse({ ...response.data, timestamp: Date.now() }));
              }
              return true;
          }),
          catchError(() => of(false))
      );
  }

}
